// Feature: features/super-admin-dashboard.md
import React, { useState } from 'react';
import {
  Box,
  Flex,
  Heading,
  Text,
  Card,
  CardHeader,
  CardBody,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Select,
  Collapse,
  HStack,
  VStack,
  Code
} from '@chakra-ui/react';
import { ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { useNavigate } from 'react-router-dom';

const ORG_CASE_ROUTE_PREFIX = {
  NMA: '/ionm-strict-analysis/',
  USN: '/usn-case/',
  MPH: '/mph-surgical-assist/'
};

const formatTimestamp = (isoString) => {
  if (!isoString) return '—';
  const target = new Date(isoString.endsWith('Z') ? isoString : `${isoString}Z`);
  return target.toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const getBadgeScheme = (val) => {
  const s = (val || '').toUpperCase();
  if (['COMPLETED', 'COMPLETE', 'SUCCESS', 'OPEN'].includes(s)) return 'green';
  if (s.includes('FAIL') || s.includes('ERROR') || s === 'INVALID' || s === 'CRITICAL') return 'red';
  if (s.includes('SKIP') || s.includes('WARN') || s.includes('PENDING') || s === 'NOT_STARTED') return 'orange';
  return 'gray';
};

const SuperAdminErrorsTriagePanel = ({ diagnostics = [], initialOrgFilter = 'ALL' }) => {
  const navigate = useNavigate();
  const [orgFilter, setOrgFilter] = useState(initialOrgFilter);
  const [categoryFilter, setCategoryFilter] = useState('ALL');
  const [expandedRows, setExpandedRows] = useState({});

  const toggleExpand = (rowKey) => {
    setExpandedRows((prev) => ({ ...prev, [rowKey]: !prev[rowKey] }));
  };

  const filtered = diagnostics.filter((item) => {
    if (orgFilter !== 'ALL' && item.org !== orgFilter) return false;
    if (categoryFilter !== 'ALL' && item.issue_category !== categoryFilter) return false;
    return true;
  });

  const handleOpenCase = (item) => {
    const prefix = ORG_CASE_ROUTE_PREFIX[item.org] || '/ionm-strict-analysis/';
    const targetId = item.org === 'NMA' ? (item.external_case_id || item.id) : (item.id || item.external_case_id);
    if (targetId) {
      navigate(`${prefix}${targetId}`);
    }
  };

  return (
    <Card shadow="sm" border="1px solid" borderColor="gray.200" bg="white">
      <CardHeader pb={3}>
        <Flex justify="space-between" align="center" flexWrap="wrap" gap={3}>
          <Box>
            <HStack spacing={2}>
              <Heading size="sm" color="gray.800">
                Uncoded Cases & Pipeline Error Diagnostics
              </Heading>
              <Badge colorScheme={filtered.length > 0 ? 'red' : 'green'}>
                {filtered.length} issues
              </Badge>
            </HStack>
            <Text fontSize="xs" color="gray.500" mt={0.5}>
              Combines Core external_cases status, file_processing_logs OCR errors, and synced worker events/logs
            </Text>
          </Box>

          <HStack spacing={2}>
            {initialOrgFilter === 'ALL' && (
              <Select size="xs" w="130px" value={orgFilter} onChange={(e) => setOrgFilter(e.target.value)}>
                <option value="ALL">All Orgs</option>
                <option value="NMA">NMA (Org 1)</option>
                <option value="USN">USNeuro (155)</option>
                <option value="MPH">MPowerHealth (203)</option>
              </Select>
            )}
            <Select size="xs" w="180px" value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="ALL">All Issue Types</option>
              <option value="UNCODED_CORE_CASE">Uncoded Core Cases</option>
              <option value="INTEGRATION_PIPELINE_ISSUE">Integration / Upload Errors</option>
            </Select>
          </HStack>
        </Flex>
      </CardHeader>

      <CardBody pt={0}>
        {filtered.length === 0 ? (
          <Flex justify="center" align="center" py={10} color="gray.500">
            <Text fontSize="sm">No uncoded cases or integration errors found in this window.</Text>
          </Flex>
        ) : (
          <Box overflowX="auto">
            <Table size="sm" variant="simple">
              <Thead bg="gray.50">
                <Tr>
                  <Th fontSize="2xs">Org</Th>
                  <Th fontSize="2xs">Case ID / Date</Th>
                  <Th fontSize="2xs">Core & File State</Th>
                  <Th fontSize="2xs">Worker & Upload</Th>
                  <Th fontSize="2xs">Primary Diagnostic Summary</Th>
                  <Th fontSize="2xs" isNumeric>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((item, idx) => {
                  const rowKey = `${item.org}-${item.id || item.external_case_id}-${idx}`;
                  const isExp = Boolean(expandedRows[rowKey]);
                  return (
                    <React.Fragment key={rowKey}>
                      <Tr
                        _hover={{ bg: 'gray.50' }}
                        cursor="pointer"
                        onClick={() => toggleExpand(rowKey)}
                      >
                        <Td>
                          <Badge
                            colorScheme={item.org === 'NMA' ? 'blue' : item.org === 'USN' ? 'purple' : 'teal'}
                            fontSize="xs"
                          >
                            {item.org}
                          </Badge>
                        </Td>
                        <Td>
                          <Text fontSize="xs" fontWeight="bold" color="gray.800">
                            {item.external_case_id || item.id}
                          </Text>
                          <Text fontSize="2xs" color="gray.500">
                            {formatTimestamp(item.date_created)}
                          </Text>
                        </Td>
                        <Td>
                          {item.issue_category === 'UNCODED_CORE_CASE' ? (
                            <HStack spacing={1} flexWrap="wrap">
                              <Badge colorScheme={item.file_count === 0 ? 'red' : 'gray'} fontSize="2xs">
                                files: {item.file_count ?? 0}
                              </Badge>
                              <Badge colorScheme={getBadgeScheme(item.cpt_status)} fontSize="2xs">
                                cpt: {item.cpt_status || 'n/a'}
                              </Badge>
                              <Badge colorScheme={getBadgeScheme(item.icd10_status)} fontSize="2xs">
                                icd10: {item.icd10_status || 'n/a'}
                              </Badge>
                              {item.case_status && item.case_status !== 'open' && (
                                <Badge colorScheme="red" fontSize="2xs">
                                  {item.case_status}
                                </Badge>
                              )}
                            </HStack>
                          ) : (
                            <Badge colorScheme="orange" fontSize="2xs">Pre-Core / Worker Issue</Badge>
                          )}
                        </Td>
                        <Td>
                          <HStack spacing={1} flexWrap="wrap">
                            {item.sync_status ? (
                              <Badge colorScheme={getBadgeScheme(item.sync_status)} fontSize="2xs">
                                {item.sync_status}
                              </Badge>
                            ) : (
                              <Text fontSize="2xs" color="gray.400">No sync row</Text>
                            )}
                            {item.sync_upload_status && (
                              <Badge colorScheme={getBadgeScheme(item.sync_upload_status)} fontSize="2xs">
                                up: {item.sync_upload_status}
                              </Badge>
                            )}
                          </HStack>
                        </Td>
                        <Td maxW="380px">
                          <Text fontSize="xs" color="red.700" fontWeight="medium" noOfLines={2}>
                            {item.primary_reason}
                          </Text>
                        </Td>
                        <Td isNumeric onClick={(e) => e.stopPropagation()}>
                          <HStack justify="flex-end" spacing={1}>
                            {(item.id || item.external_case_id) && (
                              <Button
                                size="2xs"
                                colorScheme="blue"
                                variant="outline"
                                rightIcon={<ExternalLinkIcon />}
                                onClick={() => handleOpenCase(item)}
                              >
                                Case
                              </Button>
                            )}
                            <Button
                              size="2xs"
                              variant="ghost"
                              rightIcon={isExp ? <ChevronUpIcon /> : <ChevronDownIcon />}
                              onClick={() => toggleExpand(rowKey)}
                            >
                              Detail
                            </Button>
                          </HStack>
                        </Td>
                      </Tr>

                      <Tr>
                        <Td colSpan={6} p={0} borderBottom={isExp ? '1px solid' : 'none'} borderColor="gray.200">
                          <Collapse in={isExp} animateOpacity>
                            <Box bg="gray.50" px={4} py={3} fontSize="xs">
                              <VStack align="stretch" spacing={2}>
                                <HStack spacing={4} flexWrap="wrap">
                                  <Text><b>Internal Case UUID:</b> {item.id || 'Unlinked'}</Text>
                                  <Text><b>External Case ID:</b> {item.external_case_id || '—'}</Text>
                                  <Text><b>Customer Record ID:</b> {item.customer_record_id || '—'}</Text>
                                  <Text><b>Description:</b> {item.case_description || '—'}</Text>
                                </HStack>

                                {item.case_status_reason && (
                                  <Box>
                                    <Text fontWeight="bold" color="red.700" mb={1}>Core case_status_reason:</Text>
                                    <Code p={2} borderRadius="md" display="block" whiteSpace="pre-wrap" fontSize="2xs">
                                      {JSON.stringify(item.case_status_reason, null, 2)}
                                    </Code>
                                  </Box>
                                )}

                                {item.file_errors && item.file_errors.length > 0 && (
                                  <Box>
                                    <Text fontWeight="bold" color="red.700" mb={1}>
                                      File Processing / OCR Errors ({item.file_errors.length}):
                                    </Text>
                                    {item.file_errors.map((fe, i) => (
                                      <Box key={i} bg="red.50" p={2} borderRadius="md" mb={1}>
                                        <Text fontSize="2xs">
                                          <b>{fe.file_name}</b> ({fe.operation_type} - {fe.status}): {fe.error_message}
                                        </Text>
                                      </Box>
                                    ))}
                                  </Box>
                                )}

                                {item.sync_events && item.sync_events.length > 0 && (
                                  <Box>
                                    <Text fontWeight="bold" color="orange.800" mb={1}>
                                      Integration Worker Events ({item.sync_events.length}):
                                    </Text>
                                    {item.sync_events.map((ev, i) => (
                                      <Box key={i} bg="orange.50" p={2} borderRadius="md" mb={1}>
                                        <Text fontSize="2xs" fontWeight="semibold">
                                          [{formatTimestamp(ev.timestamp)}] {ev.integration_name} → {ev.event_type} ({ev.status})
                                        </Text>
                                        <Text fontSize="2xs">{ev.message}</Text>
                                        {ev.metadata && Object.keys(ev.metadata).length > 0 && (
                                          <Code mt={1} p={1} fontSize="2xs" display="block" whiteSpace="pre-wrap">
                                            {JSON.stringify(ev.metadata, null, 2)}
                                          </Code>
                                        )}
                                      </Box>
                                    ))}
                                  </Box>
                                )}

                                {item.sync_logs && item.sync_logs.length > 0 && (
                                  <Box>
                                    <Text fontWeight="bold" color="red.800" mb={1}>
                                      Integration Worker Warning/Error Logs ({item.sync_logs.length}):
                                    </Text>
                                    {item.sync_logs.map((lg, i) => (
                                      <Box key={i} bg="red.50" p={2} borderRadius="md" mb={1}>
                                        <Text fontSize="2xs">
                                          <b>[{lg.level}] {lg.module}</b> ({formatTimestamp(lg.timestamp)}): {lg.message}
                                        </Text>
                                      </Box>
                                    ))}
                                  </Box>
                                )}
                              </VStack>
                            </Box>
                          </Collapse>
                        </Td>
                      </Tr>
                    </React.Fragment>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        )}
      </CardBody>
    </Card>
  );
};

export default SuperAdminErrorsTriagePanel;
